import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  className?: string;
  rowClassName?: (row: T) => string;
}

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  className?: string;
}

export default function DataTable<T>({ columns, rows, rowKey, className, rowClassName }: DataTableProps<T>) {
  return (
    <div className={cn('w-full overflow-x-auto', className)}>
      <table className="w-full min-w-[600px] border-collapse text-sm tabular-nums">
        <thead>
          <tr className="lb-glass-bar border-b border-background-200">
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                className={cn(
                  'px-4 py-3 text-start text-xs font-semibold uppercase tracking-wide text-foreground-500',
                  col.className,
                )}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              className={cn(
                'border-b border-background-100 transition-colors duration-150 ease-smooth last:border-0 hover:bg-background-100/70',
                rowClassName?.(row),
              )}
            >
              {columns.map((col) => (
                <td key={col.key} className={cn('px-4 py-3.5 align-middle text-foreground-800', col.className)}>
                  {col.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}